import { toast } from '@/lib/toast'

/**
 * The one pattern for recoverable deletes (IA-12, ED-51): a toast
 * "<bericht> · Ongedaan maken". A failing undo shows its own error toast.
 *
 * `view` adds a "Bekijk" button that shows what was just made (spec-65 U-035). It
 * must not take "Ongedaan maken" away, and sonner closes a toast after either of
 * its buttons, except after an `action` click that calls `preventDefault()`. So with
 * a `view` the roles swap: "Bekijk" is the action and keeps the toast open,
 * "Ongedaan maken" is the cancel button and still closes it.
 */
export function toastUndo(
  message: string,
  onUndo: () => Promise<void> | void,
  options: { view?: () => void } = {},
): void {
  const undo = {
    label: 'Ongedaan maken',
    onClick: () => {
      Promise.resolve()
        .then(onUndo)
        .catch((caught: unknown) => {
          toast.error(caught instanceof Error ? caught.message : 'Ongedaan maken mislukt.')
        })
    },
  }
  const { view } = options
  toast.success(message, {
    duration: 8000,
    ...(view
      ? {
          action: {
            label: 'Bekijk',
            onClick: (event?: { preventDefault: () => void }) => {
              event?.preventDefault()
              view()
            },
          },
          cancel: undo,
        }
      : { action: undo }),
  })
}

export interface DeferredDelete {
  /** What the toast says, e.g. "Notitie verwijderd." */
  message: string
  /** Hide the item from the screen now (it is not deleted yet). */
  hide: () => void
  /** Put the item back: on "Ongedaan maken", and when the delete fails. */
  restore: () => void
  /** The real delete request, sent once the toast is gone without an undo. */
  commit: () => Promise<unknown>
  /** The error toast when the delete fails, e.g. "Notitie verwijderen mislukt." */
  error: string
}

/**
 * Deleting with an undo window (spec-64-u2, U-098 / IA-12), without a restore endpoint:
 * the item disappears at once and a toast "… · Ongedaan maken" stays 8 seconds. Only when
 * the toast goes away without an undo (it times out, or is closed or swiped away) is the
 * delete request sent; if that fails, the item comes back with an error toast. Closing
 * the tab inside those 8 seconds sends nothing, so the item simply stays: the safe side.
 * For an item that cannot be restored, use `ConfirmDialog` instead.
 */
export function toastDeferredDelete({ message, hide, restore, commit, error }: DeferredDelete): void {
  let settled = false
  const send = () => {
    if (settled) return
    settled = true
    Promise.resolve()
      .then(commit)
      .catch((caught: unknown) => {
        restore()
        toast.error(error, {
          ...(caught instanceof Error && caught.message.trim() !== '' && caught.message !== error
            ? { description: caught.message }
            : {}),
        })
      })
  }
  hide()
  toast.success(message, {
    duration: 8000,
    action: {
      label: 'Ongedaan maken',
      onClick: () => {
        if (settled) return
        settled = true
        restore()
      },
    },
    onAutoClose: send,
    onDismiss: send,
  })
}
